import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCopy, FiDownload, FiEdit2, FiHash, FiMapPin, FiPlus, FiPower, FiPrinter, FiRefreshCw, FiX } from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const EMPTY_FORM = { name: "", outlet_id: "", catalog_id: "", status: "active" };

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function escapeHtml(value) {
  return String(value || "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  }[character]));
}

export default function Tables() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const qrPreviewRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [outletFilter, setOutletFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [qrTable, setQrTable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const limit = 50;

  const loadTables = useCallback(async (nextOffset = 0) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/qr-tables", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          outlet_id: outletFilter,
          status: statusFilter,
          limit,
          offset: nextOffset,
        },
      });
      setTables(res.data?.rows || []);
      setCount(Number(res.data?.count || 0));
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load table QR codes.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, debouncedSearchQuery, outletFilter, statusFilter]);

  const loadOptions = useCallback(async () => {
    try {
      const [outletRes, catalogRes] = await Promise.all([
        axios.get("/api/pos/outlets", { params: { business_unit_id: businessUnitId, include_inactive: true, limit: 200 } }),
        axios.get("/api/pos/catalogs/options", { params: { business_unit_id: businessUnitId } }),
      ]);
      setOutlets(outletRes.data?.rows || []);
      setCatalogs(catalogRes.data?.rows || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load table setup options.");
    }
  }, [businessUnitId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { loadTables(0); }, [loadTables]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  const availableCatalogs = useMemo(() => catalogs.filter((catalog) => (
    !form.outlet_id || !catalog.outlet_id || String(catalog.outlet_id) === String(form.outlet_id)
  )), [catalogs, form.outlet_id]);

  const selectedOutlet = useMemo(
    () => outlets.find((outlet) => String(outlet.id) === String(form.outlet_id)) || null,
    [form.outlet_id, outlets]
  );

  const openCreate = () => {
    setEditingTable(null);
    setForm(EMPTY_FORM);
    setModalMode("create");
    setError("");
  };

  const openEdit = (table) => {
    setEditingTable(table);
    setForm({ name: table.name || "", outlet_id: table.outlet_id || "", catalog_id: table.catalog_id || "", status: table.status || "active" });
    setModalMode("edit");
    setError("");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingTable(null);
    setForm(EMPTY_FORM);
  };

  const updateOutlet = (outletId) => {
    setForm((current) => {
      const currentCatalog = catalogs.find((catalog) => String(catalog.id) === String(current.catalog_id));
      const catalogMatches = !currentCatalog?.outlet_id || String(currentCatalog.outlet_id) === String(outletId);
      return { ...current, outlet_id: outletId, catalog_id: catalogMatches ? current.catalog_id : "" };
    });
  };

  const saveTable = async () => {
    if (!form.name.trim()) return setError("Table name is required.");
    if (!form.outlet_id) return setError("Select an outlet.");
    if (!selectedOutlet?.guest_order_location_ready) return setError("Configure the outlet guest ordering location first.");
    if (!form.catalog_id) return setError("Select a guest catalog.");
    try {
      setSaving(true);
      setError("");
      const payload = {
        business_unit_id: businessUnitId,
        name: form.name.trim(),
        outlet_id: form.outlet_id,
        catalog_id: form.catalog_id,
        status: form.status,
      };
      if (modalMode === "edit" && editingTable) {
        await axios.patch(`/api/pos/qr-tables/${editingTable.id}`, payload);
        setMessage(`${payload.name} updated.`);
        await loadTables(offset);
      } else {
        const res = await axios.post("/api/pos/qr-tables", payload);
        setMessage(`${payload.name} created.`);
        setQrTable(res.data || null);
        await loadTables(0);
      }
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save the QR table.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (table) => {
    const nextStatus = table.status === "active" ? "inactive" : "active";
    try {
      setActionId(table.id);
      setError("");
      await axios.patch(`/api/pos/qr-tables/${table.id}`, { business_unit_id: businessUnitId, status: nextStatus });
      setMessage(`${table.name} ${nextStatus === "active" ? "activated" : "deactivated"}.`);
      await loadTables(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update the QR table.");
    } finally {
      setActionId(null);
    }
  };

  const regenerateQr = async (table) => {
    if (!window.confirm(`Regenerate the QR for ${table.name}? The previous printed code will stop working.`)) return;
    try {
      setActionId(table.id);
      setError("");
      const res = await axios.post(`/api/pos/qr-tables/${table.id}/regenerate`, { business_unit_id: businessUnitId });
      setQrTable(res.data || null);
      setMessage(`${table.name} QR regenerated.`);
      await loadTables(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not regenerate the QR code.");
    } finally {
      setActionId(null);
    }
  };

  const copyQrUrl = async () => {
    if (!qrTable?.public_url) return;
    try {
      await navigator.clipboard.writeText(qrTable.public_url);
      setMessage("Table ordering URL copied.");
    } catch (_err) {
      setError("Could not copy the URL automatically.");
    }
  };

  const downloadQr = () => {
    const svg = qrPreviewRef.current?.querySelector("svg");
    if (!svg || !qrTable) return;
    const source = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${qrTable.outlet_name || "outlet"}-${qrTable.name || "table"}-qr.svg`.replace(/[^a-z0-9._-]+/gi, "-").toLowerCase();
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const printQr = () => {
    const svg = qrPreviewRef.current?.querySelector("svg");
    if (!svg || !qrTable) return;
    const printWindow = window.open("", "_blank", "width=520,height=700");
    if (!printWindow) return setError("Allow pop-ups to print the QR code.");
    const tableName = escapeHtml(qrTable.name);
    const outletName = escapeHtml(qrTable.outlet_name);
    printWindow.document.write(`<!doctype html><html><head><title>${tableName}</title><style>body{font-family:Arial,sans-serif;text-align:center;padding:48px;color:#111}svg{width:320px;height:320px}h1{font-size:28px;margin:24px 0 6px}p{font-size:18px;margin:0}</style></head><body>${new XMLSerializer().serializeToString(svg)}<h1>${tableName}</h1><p>${outletName}</p></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setOutletFilter("");
    setStatusFilter("");
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Tables & QR</h3>
            <div className="rm-rates-subtitle">{loading ? "Loading tables..." : `${count.toLocaleString("es-ES")} ordering points`}</div>
          </div>
          <div className="rm-topActions">
            <input className="rm-searchInput" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search table" />
            <select className="posSelect" value={outletFilter} onChange={(event) => setOutletFilter(event.target.value)}>
              <option value="">All outlets</option>
              {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.display_name}</option>)}
            </select>
            <select className="posSelect" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearFilters}><FiX /> Clear</button>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreate}><FiPlus /> Add table</button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Table ordering points</h3>
          <div className="rm-rates-subtitle">Each permanent QR opens the catalog assigned to its table.</div>
        </div>
        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">Showing {count ? offset + 1 : 0}-{Math.min(count, offset + tables.length)} of {count.toLocaleString("es-ES")}</small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={offset === 0} onClick={() => loadTables(Math.max(0, offset - limit))}>Previous</button>
            <button type="button" className="rm-topBtn" disabled={offset + limit >= count} onClick={() => loadTables(offset + limit)}>Next</button>
          </div>
        </div>
        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "240px", "--rm-table-min-width": "1350px" }}>
            <colgroup><col style={{ width: "240px" }} /><col style={{ width: "210px" }} /><col style={{ width: "230px" }} /><col style={{ width: "190px" }} /><col style={{ width: "130px" }} /><col style={{ width: "180px" }} /><col style={{ width: "370px" }} /></colgroup>
            <thead><tr>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Table</span><span className="res-thSub">Ordering point</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Outlet</span><span className="res-thSub">Routing</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Catalog</span><span className="res-thSub">Guest menu</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Access</span><span className="res-thSub">Radius · duration</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Availability</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Updated</span><span className="res-thSub">Last change</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">QR setup</span></div></th>
            </tr></thead>
            <tbody>
              {tables.map((table) => <tr key={table.id}>
                <td><strong className="posDeviceName"><FiHash /> {table.name}</strong></td>
                <td><FiMapPin /> {table.outlet_name || "-"}</td>
                <td>{table.catalog_name || "-"}</td>
                <td><span className={`rm-chip ${table.geofence_ready ? "rm-chip--ok" : "rm-chip--off"}`}>{table.geofence_ready ? `${table.radius_meters.toLocaleString("es-ES")} m · ${table.session_minutes} min` : "Location missing"}</span></td>
                <td><span className={`rm-chip ${table.status === "active" ? "rm-chip--ok" : "rm-chip--off"}`}>{table.status_label}</span></td>
                <td>{formatDate(table.updated_at)}</td>
                <td><div className="posDeviceActions">
                  <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={() => setQrTable(table)}><FiHash /> QR</button>
                  <button type="button" className="rm-topBtn" onClick={() => openEdit(table)} disabled={actionId === table.id}><FiEdit2 /> Edit</button>
                  <button type="button" className={`rm-topBtn ${table.status === "active" ? "rm-topBtn--amber" : "rm-topBtn--green"}`} onClick={() => toggleStatus(table)} disabled={actionId === table.id}><FiPower /> {table.status === "active" ? "Deactivate" : "Activate"}</button>
                  <button type="button" className="rm-topBtn" onClick={() => regenerateQr(table)} disabled={actionId === table.id}><FiRefreshCw /> Regenerate</button>
                </div></td>
              </tr>)}
              {!tables.length ? <tr><td colSpan="7" className="posMuted">No table QR codes configured.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      {modalMode ? <div className="posModalOverlay" role="presentation">
        <div className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-label="Table QR setup">
          <div className="posTicketModalHeader"><div><h3 className="rm-rates-title">{modalMode === "edit" ? "Edit table" : "Add table"}</h3><div className="rm-rates-subtitle">Assign an existing guest catalog and outlet.</div></div><button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close">×</button></div>
          <div className="posProductModalBody"><div className="posModalFormGrid">
            <label className="posModalField posModalField--wide"><span>Table name</span><input className="posTextInput" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Table 12" /></label>
            <label className="posModalField"><span>Outlet</span><select className="posSelect" value={form.outlet_id} onChange={(event) => updateOutlet(event.target.value)}><option value="">Select outlet</option>{outlets.filter((outlet) => outlet.is_active).map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.display_name}{outlet.guest_order_location_ready ? "" : " · location missing"}</option>)}</select></label>
            <label className="posModalField"><span>Guest catalog</span><select className="posSelect" value={form.catalog_id} onChange={(event) => setForm((current) => ({ ...current, catalog_id: event.target.value }))}><option value="">Select catalog</option>{availableCatalogs.map((catalog) => <option key={catalog.id} value={catalog.id}>{catalog.name}</option>)}</select></label>
            {selectedOutlet ? <div className={`posQrLocationSummary posModalField--wide ${selectedOutlet.guest_order_location_ready ? "posQrLocationSummary--ready" : ""}`}><FiMapPin /><span>{selectedOutlet.guest_order_location_ready ? `${selectedOutlet.guest_order_radius_meters} metre radius · ${selectedOutlet.guest_order_session_minutes} minute order window` : "Configure this outlet's guest ordering location before saving."}</span></div> : null}
            <label className="posCatalogToggle"><input type="checkbox" checked={form.status === "active"} onChange={(event) => setForm((current) => ({ ...current, status: event.target.checked ? "active" : "inactive" }))} />Active table</label>
          </div></div>
          <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={closeModal} disabled={saving}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveTable} disabled={saving}>{saving ? "Saving" : "Save table"}</button></div>
        </div>
      </div> : null}

      {qrTable?.public_url ? <div className="posModalOverlay" role="presentation">
        <div className="posTicketModal posQrPreviewModal" role="dialog" aria-modal="true" aria-label={`QR for ${qrTable.name}`}>
          <div className="posTicketModalHeader"><div><h3 className="rm-rates-title">{qrTable.name}</h3><div className="rm-rates-subtitle">{qrTable.outlet_name} · {qrTable.catalog_name}</div></div><button type="button" className="res-filterCloseBtn" onClick={() => setQrTable(null)} aria-label="Close">×</button></div>
          <div className="posQrPreviewBody" ref={qrPreviewRef}><div className="posQrPreviewCode"><QRCodeSVG value={qrTable.public_url} size={280} level="M" includeMargin /></div><div className="posQrPreviewDetails"><span>Permanent table URL</span><input className="posTextInput" value={qrTable.public_url} readOnly /><small>{qrTable.radius_meters.toLocaleString("es-ES")} metre radius · {qrTable.session_minutes} minute order window</small></div></div>
          <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={copyQrUrl}><FiCopy /> Copy</button><button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={downloadQr}><FiDownload /> Download SVG</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={printQr}><FiPrinter /> Print</button></div>
        </div>
      </div> : null}
    </div>
  );
}
